import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, useActiveShow, userDisplayName } from "common-fe";
import { PLACING_VIEWS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useOrgMembers } from "../hooks/useOrgMembers";
import { isLive } from "../util/live";

// #1026 — read-only Settings page for the selected show, reachable from the
// gear icon in the left nav (App.jsx navItems). Every field in the read-only
// InfoSections below comes straight off the score-show row (useActiveShow's
// activeShow, itself sourced from the generated score-common client via
// ScoreadminActiveShowProvider — no direct fetch, no new API call needed
// there since ActiveShowProvider already holds the full row).
//
// #1029 adds two live controls below the read-only sections:
//   - the per-show FVD-sync switch, MOVED here from ShowDashboard.jsx's old
//     NoFoxboxCard (#1015) — same flag/control, single home now, so it's
//     visible in every env (it's a real per-show operational switch, not a
//     demo-only feature).
//   - a demo Re-seed Show control (#1028's POST /demo/reseed), LIVE=false
//     only — see ReseedShowCard's header comment for why.
//
// managerName/secretaryName/ringDayStructure (#1034): ringDayStructure is a
// foxbox-derived display string (ring -> the show dates it runs; see
// mapShow's deriveRingDayStructure) and is populated on a re-reconciled show.
// managerName/secretaryName remain unwired — foxbox doesn't know the real FVD
// .sdf column name yet (needs an on-prem probe; see mapShow's header comment)
// — so they still render as a dash until that's confirmed, same as
// usefCompId did before #1004/#1005 wired it up.

// Parses a bare YYYY-MM-DD (score-show's startDate/endDate carry no time
// component) into a LOCAL calendar date, never through `new Date(isoString)`
// — that treats a date-only string as UTC midnight, which toLocaleDateString
// can then roll back a day in a western timezone. There's no time-of-day here
// to get wrong, just a date to not shift.
function formatShowDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function InfoRow({ label, value, testId }) {
  return (
    <Box sx={{ display: "flex", gap: 2, py: 0.75 }} data-testid={testId}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary">
        {value}
      </Typography>
    </Box>
  );
}

function InfoSection({ title, children, testId }) {
  return (
    <Paper sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="h6" gutterBottom color="text.primary">
        {title}
      </Typography>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
        {children}
      </Stack>
    </Paper>
  );
}

// #1015 — per-show "do not interact with FoxBox" toggle, MOVED here from
// ShowDashboard.jsx's NoFoxboxCard by #1029 (single home; no longer rendered
// on the dashboard). Persisted server-side on the Show row (score-api's
// setShowNoFoxbox, RMW pattern) rather than an env var, so it's a runtime,
// per-show switch: flip it and the very next FoxBox pull for this show
// (apps/foxbox/src/write/pull.js's isReleased) treats every one of its
// released scores as writeBackBlocked and never writes them back to FVD.
// Read/ingest (fvd-ingest → reconcile) is unaffected on purpose — see
// show-config.js's header comment — this only ever gates the write-back leg,
// so a flagged demo/test show stays fully scorable, it just can't push
// anything back onto FVD. Visible in every env (prod included) — this is a
// real per-show operational control, not a demo-only feature, so it's not
// LIVE-gated the way ReseedShowCard below is.
function NoFoxboxCard({ showId, initialValue, scoreApi, t }) {
  const [checked, setChecked] = useState(initialValue === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reseed from the server value whenever the active show changes (or its
  // stored value changes underneath us via the show switcher's own listShows
  // refresh) — never let a stale toggle from the PREVIOUS show bleed through.
  useEffect(() => {
    setChecked(initialValue === true);
    setError(null);
  }, [showId, initialValue]);

  const handleChange = async (event) => {
    const next = event.target.checked;
    setSaving(true);
    setError(null);
    const previous = checked;
    setChecked(next); // optimistic; reverts on failure below
    try {
      const updated = unwrap(await scoreApi.shows.setShowNoFoxbox(showId, { noFoxbox: next }));
      setChecked(updated.noFoxbox === true);
    } catch (err) {
      setChecked(previous);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="show-config-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("dashboard.config.title")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              disabled={saving}
              onChange={handleChange}
              data-testid="no-foxbox-toggle"
              inputProps={{ "aria-label": t("dashboard.config.noFoxboxLabel") }}
            />
          }
          label={t("dashboard.config.noFoxboxLabel")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.config.noFoxboxHelp")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="no-foxbox-error">
            {t("dashboard.config.noFoxboxSaveError")}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// #629 — per-show scribe unlock PIN. Same RMW pattern/home as NoFoxboxCard
// above (score-api's show-config.js, setShowScribeUnlockPin), just a text
// field instead of a switch. Both scribe-fe's PinLock (ScribeLock) and
// ScribeResume unlock with this SAME PIN (the approved design: one PIN per
// show, not a separate resume PIN) — falling back to the legacy hardcoded
// "5432" when a show has none set, so leaving this blank never locks anyone
// out. A blank save clears the field server-side (null), reverting to that
// fallback.
const PIN_PATTERN = /^[0-9]{4,8}$/;

function ScribePinCard({ showId, initialValue, scoreApi, t }) {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  useEffect(() => {
    setValue(initialValue ?? "");
    setFeedback(null);
  }, [showId, initialValue]);

  const digits = value.replace(/\D/g, "");
  const isValid = digits === "" || PIN_PATTERN.test(digits);

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setFeedback(null);
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowScribeUnlockPin(showId, {
          scribeUnlockPin: digits === "" ? null : digits,
        })
      );
      setValue(updated.scribeUnlockPin ?? "");
      setFeedback({ severity: "success", message: t("settings.scribePin.saveSuccess") });
    } catch {
      setFeedback({ severity: "error", message: t("settings.scribePin.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="scribe-pin-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.scribePin.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.scribePin.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <TextField
            size="small"
            label={t("settings.scribePin.label")}
            value={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 8))}
            error={!isValid}
            helperText={!isValid ? t("settings.scribePin.invalid") : " "}
            inputProps={{
              "data-testid": "scribe-pin-input",
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <Button
            variant="contained"
            disabled={saving || !isValid}
            onClick={handleSave}
            data-testid="scribe-pin-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.scribePin.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success" ? "scribe-pin-success" : "scribe-pin-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #1904 — per-show friendly short label (score-api's setShowFriendlyName,
// Show.friendlyName). The app bar's ShowSwitcher renders it instead of the FVD
// show name; blank clears it (null) and the full name is shown again. Same
// Save-button shape as ScribePinCard above. The cap mirrors score-api's
// MAX_SHOW_FRIENDLY_NAME_LENGTH: the label has to fit the switcher whole.
const MAX_SHOW_FRIENDLY_NAME_LENGTH = 30;

function FriendlyNameCard({ showId, initialValue, scoreApi, onSaved, t }) {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Feedback resets only on a show switch: a save pushes the new value back
  // through `initialValue` (via patchShow), which must not clear the success
  // toast it just raised.
  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);
  useEffect(() => {
    setFeedback(null);
  }, [showId]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const trimmed = value.trim();
      const updated = unwrap(
        await scoreApi.shows.setShowFriendlyName(showId, {
          friendlyName: trimmed === "" ? null : trimmed,
        })
      );
      setValue(updated.friendlyName ?? "");
      // Push the saved value into the shared active-show list so the app bar
      // switcher updates without a reload.
      onSaved?.(showId, { friendlyName: updated.friendlyName ?? null });
      setFeedback({
        severity: "success",
        message: updated.friendlyName
          ? t("settings.friendlyName.saveSuccess")
          : t("settings.friendlyName.cleared"),
      });
    } catch {
      setFeedback({ severity: "error", message: t("settings.friendlyName.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="show-friendly-name-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.friendlyName.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.friendlyName.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <TextField
            size="small"
            label={t("settings.friendlyName.label")}
            value={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.value)}
            helperText=" "
            inputProps={{
              "data-testid": "show-friendly-name-input",
              maxLength: MAX_SHOW_FRIENDLY_NAME_LENGTH,
            }}
          />
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            data-testid="show-friendly-name-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.friendlyName.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success"
                  ? "show-friendly-name-success"
                  : "show-friendly-name-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #2000 — show-level default for the Class Placings screen's view picker
// (score-common's PLACING_VIEWS, #945/#953). Same RMW pattern/home as
// ScribePinCard above (score-api's setShowDefaultPlacingView) — a Select
// instead of a text field. "System default" (the empty-string sentinel
// below) persists as `null` server-side, which resolvePlacingView
// (ClassPlacings.jsx) then falls back from to PLACING_VIEWS.DIVISION (card
// #2001) — this control itself is unaffected, it still just clears the
// show-level override back to null.
const SYSTEM_DEFAULT_VALUE = "";

function DefaultPlacingViewCard({ showId, initialValue, scoreApi, t }) {
  const [value, setValue] = useState(initialValue ?? SYSTEM_DEFAULT_VALUE);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  useEffect(() => {
    setValue(initialValue ?? SYSTEM_DEFAULT_VALUE);
    setFeedback(null);
  }, [showId, initialValue]);

  const handleChange = async (event) => {
    const next = event.target.value;
    const previous = value;
    setSaving(true);
    setFeedback(null);
    setValue(next); // optimistic; reverts on failure below
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowDefaultPlacingView(showId, {
          defaultPlacingView: next === SYSTEM_DEFAULT_VALUE ? null : next,
        })
      );
      setValue(updated.defaultPlacingView ?? SYSTEM_DEFAULT_VALUE);
      setFeedback({ severity: "success", message: t("settings.defaultPlacingView.saveSuccess") });
    } catch {
      setValue(previous);
      setFeedback({ severity: "error", message: t("settings.defaultPlacingView.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="default-placing-view-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.defaultPlacingView.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.defaultPlacingView.help")}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 260, mt: 1 }}>
          <InputLabel shrink id="default-placing-view-label">
            {t("settings.defaultPlacingView.label")}
          </InputLabel>
          <Select
            labelId="default-placing-view-label"
            label={t("settings.defaultPlacingView.label")}
            value={value}
            disabled={saving}
            onChange={handleChange}
            data-testid="default-placing-view-select"
            // "System default" is the empty-string sentinel — MUI's Select
            // treats "" as "nothing selected" and renders blank instead of
            // the MenuItem's own label unless displayEmpty is set, which
            // would otherwise show this control looking unset even when a
            // show has genuinely chosen "System default" over an explicit
            // view. displayEmpty also means the field always has visible
            // text even when "empty", so the InputLabel above needs its own
            // explicit `shrink` — MUI's automatic label-shrink logic keys
            // off the raw value being falsy, not off displayEmpty's visible
            // text, and without it the floating label sits inline and
            // overlaps the rendered value (caught by #2000's own
            // screenshot AC, cypress/e2e/settings-screenshots.cy.js).
            displayEmpty
          >
            <MenuItem value={SYSTEM_DEFAULT_VALUE} data-testid="default-placing-view-system-default">
              {t("settings.defaultPlacingView.systemDefault")}
            </MenuItem>
            <MenuItem value={PLACING_VIEWS.DEFAULT} data-testid="default-placing-view-default">
              {t("placings.view.default")}
            </MenuItem>
            <MenuItem value={PLACING_VIEWS.SCORE} data-testid="default-placing-view-score">
              {t("placings.view.score")}
            </MenuItem>
            <MenuItem value={PLACING_VIEWS.DIVISION} data-testid="default-placing-view-division">
              {t("placings.view.division")}
            </MenuItem>
            <MenuItem value={PLACING_VIEWS.SUGGESTED} data-testid="default-placing-view-suggested">
              {t("placings.view.suggested")}
            </MenuItem>
          </Select>
        </FormControl>
        <Snackbar
          open={feedback != null}
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success"
                  ? "default-placing-view-success"
                  : "default-placing-view-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

const LOGO_ACCEPT = "image/png,image/gif,image/jpeg";
const LOGO_MIN_LONG_EDGE = 600;
const LOGO_ALLOWED_TYPES = ["image/png", "image/gif", "image/jpeg"];
// Mirrors score-api's lib/logo.js LOGO_MAX_BYTES — the real ceiling is AWS
// Lambda's ~6MB synchronous invoke payload limit (a base64 body between the
// server's raw cap and that limit dies as an opaque 502/413 upstream of any
// server-side validation), so this client check is defense in depth for the
// common case, NOT the only guard — the server enforces the same cap
// independently and is what actually protects the route.
const LOGO_MAX_BYTES = 4 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("could not read file"));
    reader.readAsDataURL(file);
  });
}

function loadImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("could not read image"));
    img.src = dataUrl;
  });
}

// #1058 — per-show logo (ShowConfig #351's second consumer, alongside
// ScribePinCard above). Uploaded here as a base64 data URL and persisted on
// the Show row as `logoKey` (score-api's setShowLogo, same RMW pattern/home
// as NoFoxboxCard/ScribePinCard) — an app-owned field that survives every
// FVD re-reconcile the same way. PNG/GIF/JPEG only, minimum 600px on the
// long edge so it still looks decent shrunk to fit a report footer: checked
// here client-side for instant feedback AND server-side (the real gate — a
// client check is only ever a UX nicety, never trustworthy on its own). The
// uploaded image lives in its own private S3 bucket; this page only ever
// sees a short-lived presigned `logoUrl` the server mints fresh on every
// read (activeShow/listShows), never a permanent public link.
function LogoCard({ showId, initialLogoUrl, scoreApi, t }) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl ?? null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Re-sync to the selected show ONLY when it actually changes — skip the
  // initial mount. React flushes passive effects after paint, so a mount
  // effect that unconditionally `setFeedback(null)` can run *after* an early
  // file pick already set a validation error, wiping the toast before it's
  // seen — the source of the #1126 Cypress flake. On mount, logoUrl is already
  // seeded from the prop and feedback is already null, so there's nothing to
  // reset; only a genuine show switch needs to clear stale logo/feedback.
  const prevShowRef = useRef({ showId, initialLogoUrl });
  useEffect(() => {
    const prev = prevShowRef.current;
    if (prev.showId === showId && prev.initialLogoUrl === initialLogoUrl) return;
    prevShowRef.current = { showId, initialLogoUrl };
    setLogoUrl(initialLogoUrl ?? null);
    setFeedback(null);
  }, [showId, initialLogoUrl]);

  const applyLogo = async (image) => {
    setSaving(true);
    setFeedback(null);
    try {
      const updated = unwrap(await scoreApi.shows.setShowLogo(showId, { image }));
      setLogoUrl(updated.logoUrl ?? null);
      setFeedback({
        severity: "success",
        message: image ? t("settings.logo.saveSuccess") : t("settings.logo.removeSuccess"),
      });
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        code === "logo_too_small"
          ? t("settings.logo.errorTooSmall")
          : code === "logo_too_large"
            ? t("settings.logo.errorTooLarge")
            : code === "invalid_image_type"
              ? t("settings.logo.errorInvalidType")
              : t("settings.logo.errorGeneric");
      setFeedback({ severity: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      setFeedback({ severity: "error", message: t("settings.logo.errorInvalidType") });
      return;
    }

    if (file.size > LOGO_MAX_BYTES) {
      setFeedback({ severity: "error", message: t("settings.logo.errorTooLarge") });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { width, height } = await loadImageDimensions(dataUrl);
      if (Math.max(width, height) < LOGO_MIN_LONG_EDGE) {
        setFeedback({ severity: "error", message: t("settings.logo.errorTooSmall") });
        setSaving(false);
        return;
      }
      await applyLogo(dataUrl);
    } catch {
      setFeedback({ severity: "error", message: t("settings.logo.errorGeneric") });
      setSaving(false);
    }
  };

  const handleRemove = () => applyLogo(null);

  return (
    <Card variant="outlined" data-testid="show-logo-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.logo.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.logo.help")}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 1 }}
          alignItems="center"
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={t("settings.logo.currentAlt")}
              data-testid="show-logo-preview"
              sx={{
                maxHeight: 80,
                maxWidth: 200,
                objectFit: "contain",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 0.5,
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="show-logo-empty">
              {t("settings.logo.noneUploaded")}
            </Typography>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              component="label"
              disabled={saving}
              data-testid="show-logo-upload-button"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {logoUrl ? t("settings.logo.replaceButton") : t("settings.logo.uploadButton")}
              <input
                type="file"
                accept={LOGO_ACCEPT}
                hidden
                onChange={handleFileChange}
                data-testid="show-logo-file-input"
              />
            </Button>
            {logoUrl && (
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={handleRemove}
                data-testid="show-logo-remove-button"
              >
                {t("settings.logo.removeButton")}
              </Button>
            )}
          </Stack>
        </Stack>

        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={feedback.severity === "success" ? "show-logo-success" : "show-logo-error"}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #2379 — the demo org has exactly one scribe role.
const SCRIBE_ROLE = "score:score_scribe";

// #1029 — demo Re-seed Show control, wired to #1028's POST /demo/reseed.
// LIVE=false only (see util/live.js's isLive header comment for why the
// backend is the real authority and this client gate is UX-only): the
// endpoint itself hard-refuses with 403 live_guard when LIVE=true and 403
// prod_refused on ENVIRONMENT_NAME=main, so this control simply isn't worth
// showing where it can never succeed.
//
// #2379 rework: the three baked-dump `state` presets and the client-supplied
// `startDate` are GONE from the contract (Jaybill's decision: one seed only,
// the start date is pulled server-side off the already-ingested show's own
// startDate) — there is nothing left here for a dropdown or a date picker to
// control, so both are removed rather than left as dead controls that don't
// reach the server.
//
// scribeUserIds is new and is this card's headline capability: score-api's
// Lambda role only reaches score-* tables (see lib/demo-scribe-assignments.js
// header), so it cannot look up org-demo's membership itself. scoreadmin-fe
// already has account-api access via useOrgMembers, so THIS is where the
// scribe list comes from — never inferred from an email domain (a demo user
// can sit on a different org's real UUID), always the active org's real
// membership, filtered to the scribe role.
//
// Exported only for Settings.reseedRefetch.test.jsx (#2857), which renders it
// directly to reach the token-renew refetch window Cypress cannot.
export function ReseedShowCard({ scoreApi, t }) {
  const { activeOrgId } = useActiveOrg();
  const { members: orgMembers, loading: membersLoading } = useOrgMembers(activeOrgId);
  const scribes = orgMembers.filter((m) => (m.roles || []).includes(SCRIBE_ROLE));

  const [scribeUserIds, setScribeUserIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Default to "assign every available scribe" once the org member list
  // loads, so the common case (reseed + assign, one click) needs no manual
  // selection — the multi-select below still lets an operator narrow it.
  const scribeIdsKey = scribes.map((m) => m.userId).join(",");
  useEffect(() => {
    setScribeUserIds(scribes.map((m) => m.userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribeIdsKey]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = unwrap(
        await scoreApi.demo.reseedDemo({
          scribeUserIds,
        })
      );
      setFeedback({
        severity: "success",
        message: t("settings.reseed.success", {
          classesMerged: result.applied?.classesMerged ?? 0,
          ridesMerged: result.applied?.ridesMerged ?? 0,
          assignmentCount: result.scribes?.assignmentCount ?? 0,
        }),
      });
    } catch (err) {
      setFeedback({ severity: "error", message: err.message || t("settings.reseed.error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="reseed-show-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.reseed.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.reseed.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="reseed-scribes-label">{t("settings.reseed.scribesLabel")}</InputLabel>
            <Select
              labelId="reseed-scribes-label"
              label={t("settings.reseed.scribesLabel")}
              multiple
              value={scribeUserIds}
              disabled={submitting || membersLoading}
              onChange={(event) => setScribeUserIds(event.target.value)}
              data-testid="reseed-scribes-select"
              renderValue={(selected) =>
                scribes
                  .filter((m) => selected.includes(m.userId))
                  .map((m) => userDisplayName(m))
                  .join(", ")
              }
            >
              {scribes.map((m) => (
                <MenuItem key={m.userId} value={m.userId}>
                  <Checkbox checked={scribeUserIds.includes(m.userId)} />
                  <ListItemText primary={userDisplayName(m)} />
                </MenuItem>
              ))}
            </Select>
            {!membersLoading && scribes.length === 0 ? (
              <FormHelperText data-testid="reseed-no-scribes">{t("settings.reseed.noScribes")}</FormHelperText>
            ) : null}
          </FormControl>

          <Button
            variant="contained"
            disabled={submitting || membersLoading || scribeUserIds.length === 0}
            onClick={handleSubmit}
            data-testid="reseed-submit-button"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.reseed.submit")}
          </Button>
        </Stack>

        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={feedback.severity === "success" ? "reseed-success" : "reseed-error"}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.settings"));
  const { activeShowId, activeShow, patchShow, loading: loadingShows } = useActiveShow();
  const scoreApi = useScoreApi();

  const na = t("settings.notAvailable");

  // MD3 loading convention (#514): shaped skeleton, not a blank flash.
  if (loadingShows) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("settings.title")}
        </Typography>
        <TableSkeleton rows={1} columns={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("settings.title")}
      </Typography>

      {!activeShowId && (
        <Typography color="text.secondary">{t("settings.noShows")}</Typography>
      )}

      {activeShowId && (
        <Stack spacing={3} data-testid="settings-content" sx={{ maxWidth: 720 }}>
          <InfoSection title={t("settings.sections.showInfo")} testId="settings-show-info">
            <InfoRow
              label={t("settings.fields.name")}
              value={activeShow?.name ?? na}
              testId="settings-field-name"
            />
            <InfoRow
              label={t("settings.fields.organization")}
              value={activeShow?.organization ?? na}
              testId="settings-field-organization"
            />
            <InfoRow
              label={t("settings.fields.location")}
              value={activeShow?.location ?? na}
              testId="settings-field-location"
            />
            <InfoRow
              label={t("settings.fields.startDate")}
              value={formatShowDate(activeShow?.startDate) ?? na}
              testId="settings-field-start-date"
            />
            <InfoRow
              label={t("settings.fields.endDate")}
              value={formatShowDate(activeShow?.endDate) ?? na}
              testId="settings-field-end-date"
            />
          </InfoSection>

          <InfoSection title={t("settings.sections.usef")} testId="settings-usef">
            <InfoRow
              label={t("settings.fields.usefShowId")}
              value={activeShow?.usefCompId ?? na}
              testId="settings-field-usef-show-id"
            />
            <InfoRow
              label={t("settings.fields.usefCompYear")}
              value={activeShow?.usefCompYear ?? na}
              testId="settings-field-usef-comp-year"
            />
          </InfoSection>

          <InfoSection title={t("settings.sections.management")} testId="settings-management">
            <InfoRow
              label={t("settings.fields.manager")}
              value={activeShow?.managerName ?? na}
              testId="settings-field-manager"
            />
            <InfoRow
              label={t("settings.fields.secretary")}
              value={activeShow?.secretaryName ?? na}
              testId="settings-field-secretary"
            />
          </InfoSection>

          <InfoSection title={t("settings.sections.structure")} testId="settings-structure">
            <InfoRow
              label={t("settings.fields.ringDayStructure")}
              value={activeShow?.ringDayStructure ?? na}
              testId="settings-field-ring-day-structure"
            />
          </InfoSection>

          <LogoCard
            showId={activeShowId}
            initialLogoUrl={activeShow?.logoUrl}
            scoreApi={scoreApi}
            t={t}
          />

          <NoFoxboxCard
            showId={activeShowId}
            initialValue={activeShow?.noFoxbox}
            scoreApi={scoreApi}
            t={t}
          />

          <FriendlyNameCard
            showId={activeShowId}
            initialValue={activeShow?.friendlyName}
            scoreApi={scoreApi}
            onSaved={patchShow}
            t={t}
          />

          <ScribePinCard
            showId={activeShowId}
            initialValue={activeShow?.scribeUnlockPin}
            scoreApi={scoreApi}
            t={t}
          />

          <DefaultPlacingViewCard
            showId={activeShowId}
            initialValue={activeShow?.defaultPlacingView}
            scoreApi={scoreApi}
            t={t}
          />
        </Stack>
      )}

      {/* card #2379 round 8: was previously the last child of the
          `activeShowId &&` Stack above, so it could never render on a
          genuinely empty org (zero shows) -- exactly the state reseed exists
          to fix from scratch. Every existing spec's GET /show intercept
          always returns one show, so this never went red locally; it only
          surfaced hitting the real, freshly-deployed (zero-show) demo env.
          Moved outside both the activeShowId and !activeShowId branches so
          it renders in either state, still gated on !isLive() only. */}
      {!isLive() && <ReseedShowCard scoreApi={scoreApi} t={t} />}
    </Box>
  );
}
